// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { StoreHealthMetric, StoreHealthPanelName } from "@/api/hooks/use-store-health";
import { STORE_HEALTH_PANELS, useStoreHealthPanelQuery } from "@/api/hooks/use-store-health";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface StoreHealthTabProps {
  storeName: string;
}

/**
 * Tile tint per grade. Deliberately quieter than the scorecard's badges: a health panel is
 * a wall of thirty-odd numbers, and a green tile on every healthy metric would leave nothing
 * for a red one to stand out against. Healthy is plain; only trouble is coloured.
 */
const INDICATOR_TILE: Record<string, string> = {
  success: "",
  warning: "bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-900",
  error: "bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-900",
};

const INDICATOR_VALUE: Record<string, string> = {
  success: "",
  warning: "text-amber-700 dark:text-amber-300",
  error: "text-red-700 dark:text-red-300",
};

/** Panels in three columns, matching how Impact groups them. */
const PANEL_COLUMNS: StoreHealthPanelName[][] = [
  ["cameras", "boxes"],
  ["mods", "match"],
  ["upcs", "action-tool", "field-tool"],
];

// Rules go between panels, never above the one that opens a column: `*+*` is every panel
// that has one above it inside the same column.
const PANEL_STACK = "flex-1 space-y-6 [&>*+*]:border-t [&>*+*]:pt-6";

// Half the gutter each, so a rule runs out to the seam and the two meet in a T instead of
// the horizontal one stopping 24px short of the vertical. The margin widens the box into
// the gutter; the matching padding puts the content back. The middle column reaches for a
// seam on both sides.
const RULE_TO_SEAM = [
  "xl:[&>*+*]:-mr-6 xl:[&>*+*]:pr-6",
  "xl:[&>*+*]:-mx-6 xl:[&>*+*]:px-6",
  "xl:[&>*+*]:-ml-6 xl:[&>*+*]:pl-6",
];

// The two seams, each as tall as the tallest column. Borders on the columns themselves
// would stop where that column's content does, which on a page whose whole point is uneven
// columns is exactly where they must not stop.
//
// The scorecard puts its single seam at `left-1/2`, which is only exact because two equal
// columns and one gap put the midpoint of the container and the midpoint of the gutter in
// the same place. With three columns that coincidence is gone: a column is
// `(100% - 2*gap)/3` wide, so the first gutter centre lands at `100%/3 - gap/6`, not at
// `100%/3`. At `gap-12` (3rem) that is half a rem of drift — enough to read as crooked
// against the horizontal rules it is supposed to meet.
//
// `-bottom-4` runs both seams through the parent's `space-y-4` so they land on the footer's
// top border, closing the T at the bottom the same way the panel rules close theirs at the
// sides. The gap itself is untouched — only the lines grow.
// Both seams are written out literally rather than built from a shared fragment: Tailwind
// generates utilities by scanning source text, so a class assembled at runtime produces no
// CSS at all and the seam would simply not be there.
const PANEL_LAYOUT = cn(
  "flex flex-col gap-6 xl:flex-row xl:gap-12",
  "xl:relative",
  "xl:before:absolute xl:before:top-0 xl:before:-bottom-4 xl:before:w-px xl:before:bg-border xl:before:content-['']",
  "xl:before:left-[calc(100%/3-0.5rem)]",
  "xl:after:absolute xl:after:top-0 xl:after:-bottom-4 xl:after:w-px xl:after:bg-border xl:after:content-['']",
  "xl:after:left-[calc(200%/3+0.5rem)]",
);

// Stacked on a narrow screen the columns run together, so every column after the first
// needs the rule its opening panel is deliberately missing.
const COLUMN_STACKED = "border-t pt-6 xl:border-t-0 xl:pt-0";

/**
 * Reduce a span to its largest whole unit, the way Impact prints mod ages.
 *
 * "18 hours" rather than "17 hours 42 minutes 3 seconds": these are staleness indicators
 * read at a glance, and the extra precision is noise that costs a line wrap in a tile.
 */
function formatSpan(seconds: number, t: ReturnType<typeof useTranslation>["t"]): string {
  const units: [number, string][] = [
    [86400, "day"],
    [3600, "hour"],
    [60, "minute"],
  ];
  for (const [size, unit] of units) {
    if (seconds >= size) {
      const count = Math.floor(seconds / size);
      return t(`storeHealth.units.${unit}`, { count });
    }
  }
  return t("storeHealth.units.second", { count: Math.max(0, Math.round(seconds)) });
}

/**
 * Categorical series colours, assigned to segments in this fixed order and never cycled.
 *
 * Written out as whole class names because Tailwind generates utilities by scanning source
 * text — `bg-chart-${i}` would compile to nothing and the bar would render colourless.
 * The backend caps a breakdown at this many parts and folds the rest into `other`, so the
 * list is never exhausted and a segment's colour never depends on how many siblings it has.
 */
const SERIES_FILL = [
  "bg-chart-1",
  "bg-chart-2",
  "bg-chart-3",
  "bg-chart-4",
  "bg-chart-5",
  "bg-chart-6",
  "bg-chart-7",
  "bg-chart-8",
] as const;

/** A proportion drawn as a bar — used where the share matters more than the digits. */
function RatioBar({ metric, indicator }: { metric: StoreHealthMetric; indicator: string }) {
  const covered = Math.round((metric.value ?? 0) * 1000) / 10;
  // The track and fill are drawn in the tile's OWN colour rather than fixed greys and
  // brand green. On a red or amber tile a neutral grey track reads as a foreign block
  // sitting on the tint, and a green fill argues with the very grade that turned the tile
  // red. `currentColor` inherits whatever the indicator set, so the bar always belongs to
  // the tile it is in; on an ungraded tile that resolves to plain ink and the fill keeps
  // the brand green.
  const graded = indicator === "warning" || indicator === "error";
  return (
    <div className="space-y-1.5">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-current/15">
        <div
          className={cn("transition-[width] duration-150", graded ? "bg-current" : "bg-primary")}
          style={{ width: `${covered}%` }}
        />
      </div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-medium tabular-nums">{covered.toFixed(1)}%</span>
        <span className="text-muted-foreground tabular-nums">
          {metric.numerator ?? 0} / {metric.denominator ?? 0}
        </span>
      </div>
    </div>
  );
}

/**
 * A total split into named parts, drawn as one stacked bar with a legend.
 *
 * The legend is not optional decoration: three of the light-mode series colours sit below
 * 3:1 contrast against a white surface, so identity and value have to be readable as text
 * beside the swatch rather than inferred from the fill.
 */
function BreakdownBar({ metric }: { metric: StoreHealthMetric }) {
  const { t } = useTranslation();
  const parts = metric.parts ?? [];
  const total = parts.reduce((sum, part) => sum + part.value, 0);

  if (total === 0) {
    return <span className="text-muted-foreground text-sm">{t("storeHealth.nothingWorked")}</span>;
  }

  return (
    <div className="space-y-2.5">
      {/* `gap-0.5` is the 2px surface gap that separates touching segments — the separation
          is done by the surface showing through, not by a stroke around each fill, which
          would add ink that is not data. Only the outer ends are rounded, so the bar reads
          as one measure rather than a row of pills. */}
      <div className="flex h-2.5 w-full gap-0.5">
        {parts.map((part, index) => (
          <Tooltip key={part.key}>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "min-w-0.5 first:rounded-l-[4px] last:rounded-r-[4px]",
                  SERIES_FILL[index % SERIES_FILL.length],
                )}
                style={{ flexGrow: part.value, flexBasis: 0 }}
              />
            </TooltipTrigger>
            <TooltipContent side="top">
              {partLabel(part.key, t)}: {part.value} ({((part.value / total) * 100).toFixed(1)}%)
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1">
        {parts.map((part, index) => (
          <li key={part.key} className="flex items-center gap-1.5 text-xs">
            {/* The swatch carries identity; the text stays in text tokens, because a pale
                categorical hue is illegible as type on the surface. */}
            <span
              aria-hidden
              className={cn("size-2 shrink-0 rounded-[2px]", SERIES_FILL[index % SERIES_FILL.length])}
            />
            <span className="text-muted-foreground">{partLabel(part.key, t)}</span>
            <span className="font-medium tabular-nums">{part.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Ops task types come straight from a backend enum, so they are humanised, not translated. */
function partLabel(key: string, t: ReturnType<typeof useTranslation>["t"]): string {
  if (key === "other") return t("storeHealth.otherPart");
  return key.replace(/_/g, " ");
}

function MetricValue({ metric }: { metric: StoreHealthMetric }) {
  const { t } = useTranslation();
  if (metric.value === null || metric.value === undefined) {
    return <span className="text-muted-foreground text-xl">—</span>;
  }
  if (metric.format === "duration") {
    return <span className="font-semibold text-xl tabular-nums">{formatSpan(metric.value, t)}</span>;
  }
  if (metric.format === "percent") {
    return (
      <span className="font-semibold text-xl tabular-nums">
        {metric.value.toFixed(2)}
        <span className="ml-0.5 font-normal text-muted-foreground text-sm">%</span>
      </span>
    );
  }
  return <span className="font-semibold text-xl tabular-nums">{metric.value.toLocaleString()}</span>;
}

/**
 * One measurement. The label names it, the number answers it, and the ratio behind the
 * number lives in the tooltip — a store with 1016 unpaired boxes of 26,895 reads as "3.78%"
 * until you need to know how big the job actually is.
 */
function MetricTile({ panel, metric }: { panel: StoreHealthPanelName; metric: StoreHealthMetric }) {
  const { t } = useTranslation();
  const indicator = metric.indicator ?? "success";
  // Labels are namespaced by panel because the keys collide by design: `uptime` is camera
  // capture on one panel and shelf readability on another, and `active` is a camera count
  // here and a mod ratio there. A flat lookup would label one of each pair wrongly.
  // A metric the backend adds before its translation lands falls back to a humanised key
  // rather than printing a raw path at someone.
  const label = t(`storeHealth.metrics.${panel}.${metric.key}`, { defaultValue: metric.key.replace(/_/g, " ") });

  // Both bar forms want the full row: squeezed into half of one, a ratio's fill stops being
  // readable and a breakdown's legend wraps to a column of single entries.
  const isBar = metric.format === "ratio" || metric.format === "breakdown";

  const tile = (
    <div
      className={cn(
        "flex min-w-0 flex-col justify-between gap-2 rounded-md border p-3",
        // Impact draws these the same way — its two bar charts are full-width blocks
        // while every other reading is a half-width card.
        isBar && "col-span-2",
        INDICATOR_TILE[indicator],
        INDICATOR_VALUE[indicator],
      )}
    >
      <span className="truncate font-medium text-muted-foreground text-xs">{label}</span>
      {metric.format === "breakdown" ? (
        <BreakdownBar metric={metric} />
      ) : metric.format === "ratio" ? (
        <RatioBar metric={metric} indicator={indicator} />
      ) : (
        <MetricValue metric={metric} />
      )}
    </div>
  );

  // A bar carries its own workings — the ratio prints them under the fill, the breakdown
  // gives every segment its own tooltip — so wrapping the whole tile would only shadow them.
  if (metric.denominator === null || metric.denominator === undefined || isBar) {
    return tile;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>{tile}</TooltipTrigger>
      <TooltipContent side="top">
        {t("storeHealth.ofTotal", { numerator: metric.numerator ?? 0, denominator: metric.denominator })}
      </TooltipContent>
    </Tooltip>
  );
}

/** One panel. Loads, errors and refetches entirely on its own. */
function HealthPanelSection({ storeName, panel }: { storeName: string; panel: StoreHealthPanelName }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch, isFetching } = useStoreHealthPanelQuery(storeName, panel);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{t(`storeHealth.panels.${panel}.title`)}</h3>
          <p className="text-muted-foreground text-sm">{t(`storeHealth.panels.${panel}.description`)}</p>
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label={t("storeHealth.refreshPanel", { panel: t(`storeHealth.panels.${panel}.title`) })}
        >
          <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
        </Button>
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 gap-2">
          <Skeleton className="h-[72px]" />
          <Skeleton className="h-[72px]" />
          <Skeleton className="h-[72px]" />
          <Skeleton className="h-[72px]" />
        </div>
      )}

      {isError && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
          <span className="flex items-center gap-2 text-destructive text-sm">
            <TriangleAlert className="h-4 w-4 shrink-0" />
            {t("storeHealth.panelError")}
          </span>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? <InlineSpinner /> : <RefreshCw className="h-3.5 w-3.5" />}
            {t("storeHealth.retry")}
          </Button>
        </div>
      )}

      {data && data.metrics.length === 0 && (
        <p className="text-muted-foreground text-sm">{t("storeHealth.noMetrics")}</p>
      )}

      {data && data.metrics.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {data.metrics.map((metric) => (
            <MetricTile key={metric.key} panel={panel} metric={metric} />
          ))}
        </div>
      )}
    </section>
  );
}

export function StoreHealthTab({ storeName }: StoreHealthTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const healthQueryKey = ["stores", "health", storeName];

  const isRefreshing = useIsFetching({ queryKey: healthQueryKey }) > 0;

  // Read straight off the cache rather than held in state: `useIsFetching` above re-renders
  // on every fetch transition, which is exactly when this changes.
  const checkedStamps = queryClient
    .getQueryCache()
    .findAll({ queryKey: healthQueryKey })
    .map((query) => query.state.dataUpdatedAt)
    .filter((stamp) => stamp > 0);
  const lastCheckedAt = checkedStamps.length > 0 ? Math.max(...checkedStamps) : null;

  return (
    <div className="space-y-4 p-6">
      <div className={PANEL_LAYOUT}>
        {PANEL_COLUMNS.map((column, columnIndex) => (
          <div
            key={column.join()}
            className={cn(PANEL_STACK, RULE_TO_SEAM[columnIndex], columnIndex > 0 && COLUMN_STACKED)}
          >
            {column.map((panel) => (
              <HealthPanelSection key={panel} storeName={storeName} panel={panel} />
            ))}
          </div>
        ))}
      </div>

      {/* Footer bar mirroring the scorecard: icon-only refresh on the left, with the
          freshness of what you are looking at beside it. */}
      <div className="flex items-center gap-2 border-t pt-4">
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9"
          onClick={() => queryClient.invalidateQueries({ queryKey: healthQueryKey })}
          disabled={isRefreshing}
          aria-label={t("storeHealth.refreshAll")}
        >
          <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
        </Button>
        <p className="text-muted-foreground text-sm">
          {isRefreshing
            ? t("common.loading")
            : lastCheckedAt
              ? t("storeHealth.lastChecked", {
                  time: formatDistanceToNow(new Date(lastCheckedAt), { addSuffix: true }),
                })
              : null}
        </p>
      </div>
    </div>
  );
}

export { STORE_HEALTH_PANELS };
